import React, { useState, useEffect } from 'react';
import { Upload, FileText, Trash2, Globe } from 'lucide-react';
import ApiClient from '../utils/api';

const DocumentUpload = () => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [metadata, setMetadata] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [scraping, setScraping] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const kbResponse = await ApiClient.getKnowledgeDocuments();
      const dbResponse = await ApiClient.getDocuments();
      
      // Merge to get proper database IDs
      const kbDocs = kbResponse.documents || [];
      const dbDocs = dbResponse.documents || [];
      
      const mergedDocs = kbDocs.map(kbDoc => {
        const dbDoc = dbDocs.find(db => db.original_name === kbDoc.name);
        return {
          ...kbDoc,
          dbId: dbDoc?.id // Add database ID for deletion
        };
      });
      
      setDocuments(mergedDocs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const metadataObj = metadata ? { metadata: metadata } : {};
      const response = await ApiClient.uploadDocuments(files, metadataObj);
      
      // Immediately add uploaded files with processing status
      const uploadedFiles = response.files || [];
      const processingDocs = uploadedFiles.map(file => ({
        name: file.filename,
        chunks: 'Processing...',
        status: 'processing',
        dbId: file.id
      }));
      
      setDocuments(prev => [...prev, ...processingDocs]);
      
      alert(`Uploaded ${files.length} document(s)${metadata ? ' with metadata' : ''}. Processing started automatically.`);
      
      // Start polling for completion
      uploadedFiles.forEach(file => {
        pollDocumentStatus(file.id);
      });
      
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const pollDocumentStatus = async (docId) => {
    const checkStatus = async () => {
      try {
        const statusResponse = await ApiClient.getDocumentStatus(docId);
        const kbResponse = await ApiClient.getKnowledgeDocuments();
        
        // Check if document appears in knowledge base (means it's completed)
        const docInKB = kbResponse.documents?.find(doc => doc.name === statusResponse.name);
        
        if (statusResponse.status === 'completed' || docInKB) {
          // Refresh to get final chunk count
          await loadDocuments();
        } else {
          setTimeout(checkStatus, 5000);
        }
      } catch (error) {
        console.error('Status polling failed:', error);
        // Continue polling even on error
        setTimeout(checkStatus, 1000);
      }
    };
    
    setTimeout(checkStatus, 100);
  };

  const removeDocument = async (docId) => {
    try {
      await ApiClient.deleteDocument(docId);
      await loadDocuments();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const handleWebsiteScrape = async () => {
    if (!websiteUrl.trim()) {
      alert('Please enter a website URL');
      return;
    }

    setScraping(true);
    try {
      const response = await ApiClient.request('/documents/add-website', {
        method: 'POST',
        body: JSON.stringify({
          url: websiteUrl,
          max_depth: 2,
          max_links: 10
        })
      });
      
      alert(response.message || 'Website content scraped successfully!');
      setWebsiteUrl('');
      
      // Refresh documents after scraping
      setTimeout(() => {
        loadDocuments();
      }, 2000);
      
    } catch (error) {
      console.error('Website scraping failed:', error);
      alert('Website scraping failed. Please check the URL and try again.');
    } finally {
      setScraping(false);
    }
  };

  return (
    <div className="container">
      <h1>Document Upload & Vectorization</h1>
      
      <div className="card">
        <h3>Upload Documents</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>Metadata (optional)</label>
          <textarea
            placeholder="Enter metadata for documents (e.g., category: manual, department: IT, tags: training, policy)"
            value={metadata}
            onChange={(e) => setMetadata(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>
        
        <div 
          style={{ 
            border: '2px dashed #ddd', 
            padding: '40px', 
            textAlign: 'center', 
            borderRadius: '4px',
            cursor: uploading ? 'not-allowed' : 'pointer',
            background: uploading ? '#f9f9f9' : 'transparent',
            marginBottom: '20px'
          }}
          onClick={() => !uploading && document.getElementById('doc-input').click()}
        >
          {uploading ? (
            <>
              <div style={{ 
                width: '24px', 
                height: '24px', 
                border: '3px solid #ccc', 
                borderTop: '3px solid #007bff', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }}></div>
              <p>Uploading...</p>
            </>
          ) : (
            <>
              <Upload size={48} color="#999" />
              <p>Click to upload documents</p>
              <p style={{ fontSize: '12px', color: '#666' }}>Supported formats: PDF, DOC, DOCX, TXT</p>
              {metadata && (
                <p style={{ fontSize: '11px', color: '#007bff', marginTop: '8px' }}>
                  ✓ Metadata will be included with upload
                </p>
              )}
            </>
          )}
        </div>

        <input
          id="doc-input"
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
      </div>

      <div className="card">
        <h3>Scrape Website Content</h3>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>Website URL</label>
            <input
              type="url"
              placeholder="https://example.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
          
          <button
            onClick={handleWebsiteScrape}
            disabled={scraping || !websiteUrl.trim()}
            style={{
              background: scraping || !websiteUrl.trim() 
                ? '#95a5a6' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '4px',
              cursor: scraping || !websiteUrl.trim() ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap'
            }}
          >
            {scraping ? (
              <>
                <div style={{ 
                  width: '16px', 
                  height: '16px', 
                  border: '2px solid transparent', 
                  borderTop: '2px solid white', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite' 
                }}></div>
                Scraping...
              </>
            ) : (
              <>
                <Globe size={16} />
                Scrape Website
              </>
            )}
          </button>
        </div>
        
        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          This will scrape up to 10 pages with a maximum depth of 2 levels from the provided URL.
        </p>
      </div>

      {documents.length > 0 && (
        <div className="card">
          <h3>Uploaded Documents</h3>
          
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #ddd' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Document</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Chunks</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc, index) => (
                  <tr key={doc.id || index} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={20} />
                        <span>{doc.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {doc.status === 'processing' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            width: '12px', 
                            height: '12px', 
                            border: '2px solid #856404', 
                            borderTop: '2px solid transparent', 
                            borderRadius: '50%', 
                            animation: 'spin 1s linear infinite' 
                          }}></div>
                          <span style={{ fontSize: '12px', color: '#856404' }}>Processing...</span>
                        </div>
                      ) : (
                        doc.chunks || 'N/A'
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {doc.status === 'processing' ? (
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          fontSize: '12px',
                          background: '#fff3cd',
                          color: '#856404'
                        }}>
                          Processing
                        </span>
                      ) : (
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          fontSize: '12px',
                          background: '#d4edda',
                          color: '#155724'
                        }}>
                          Completed
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <button 
                        onClick={() => removeDocument(doc.dbId)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: 'red', 
                          cursor: 'pointer' 
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

};

export default DocumentUpload;